import { TEXTURE_ART, elem, styleInstaller } from "./chrome";

const STYLE_ID = "metin3-help-style";

/** `helpwindow.py`'s float text rows, `[row, text]`: row `n` sits at
 *  `SCREEN_HEIGHT * (50 + 20n) / 600` (`:4-5`, `:14-118`), in the English
 *  pack's words (`locale_interface.txt:144-171`). Two rows name keys this client
 *  does not bind and are not drawn: row 5, `HELP_OPEN_WHISPER` ("Shift + Enter",
 *  `OpenWhisperDialogWithoutTarget`, `game.py:1551-1555`), and row 16,
 *  `HELP_SCREEN_CAPTURE` (`SaveScreen` on `DIK_SYSRQ`, `game.py:337`, writes a
 *  file a page cannot). */
const HELP_ROWS: readonly (readonly [number, string])[] = [
  [0, "Panel: W, A, S, D or arrow keys"], // HELP_MOVE_KEY, locale_interface.txt:158
  [1, "Camera View: right or middle mouse button"], // HELP_CONTROL_CAMERA_BY_RIGHTBUTTON, :149
  [2, "Camera View: middle or right mouse button"], // HELP_CONTROL_CAMERA_BY_MIDDLEBUTTON, :148
  [3, "Display Names: Alt"], // HELP_SHOW_ALL_NAME, :171
  [4, "Open chat window: Enter"], // HELP_OPEN_CHAT, :160
  [6, "Attack: left mouse button or space bar"], // HELP_ATTACK_KEY, :144
  [7, "Open character window: C"], // HELP_OPEN_CHARACTER, :159
  [8, "Open Skill Window: V"], // HELP_OPEN_SKILL, :165
  [9, "Open Task Window: N"], // HELP_OPEN_QUEST, :164
  [10, "Open inventory window: I"], // HELP_OPEN_INVENTORY, :161
  [11, "Open Chatlog: L"], // HELP_OPEN_LOG, :162
  [12, "Open Large Map: M"], // HELP_OPEN_ZONEMAP, :167
  [13, "Open mini map: Shift + M"], // HELP_OPEN_MINIMAP, :163
  [14, "Change attack mode: change setting of left mouse button"], // HELP_CHANGE_PK_MODE, :145
  // HELP_PICK_ITEM (:168) says "^ or Y", the German keys in the places of
  // DIK_GRAVE and DIK_Z (game.py:364-365); this names the keys the client binds.
  [15, "Collect Items: ` or Z or left mouse button"],
  [17, "Open Guild Window: Alt + G"], // HELP_GUILD_WINDOW, :152
  [18, "Open Friends List: Alt + M"], // HELP_MESSENGER_WINDOW, :155
  [19, "Display help: H"], // HELP_HELP, :153
];

/** The float text lines, top to bottom. */
export const HELP_LINES: readonly string[] = HELP_ROWS.map(([, text]) => text);

const STYLE = `
/* HelpWindow is SCREEN_WIDTH x SCREEN_HEIGHT on TOP_MOST (helpwindow.py:9-12,
   uihelp.py:10), opened over wndUICurtain, a TOP_MOST Bar of 0x77000000
   (interfacemodule.py:92-97, :1082-1084). */
.help-window {
  position: fixed;
  inset: 0;
  z-index: 75;
  background: rgba(0, 0, 0, ${0x77 / 255});
  pointer-events: auto;
  color: #fff;
  font: 12px/14px Tahoma, "Segoe UI", sans-serif;
}
.help-window[hidden] { display: none; }
.help-window:focus { outline: none; }
/* "outline" : 1 text at x = SCREEN_WIDTH * 150 / 800 (helpwindow.py:14-118). */
.help-line {
  position: absolute;
  left: ${150 / 8}%;
  white-space: nowrap;
  text-shadow: -1px 0 #000, 1px 0 #000, 0 -1px #000, 0 1px #000;
}
/* close_button at SCREEN_WIDTH * 55 / 800, SCREEN_HEIGHT * 55 / 600 on
   xlarge_thin_button (helpwindow.py:264-275), 57x30. */
.help-close {
  position: absolute;
  left: ${55 / 8}%;
  top: ${55 / 6}%;
  width: 57px;
  height: 30px;
  padding: 0;
  border: 0;
  background: url("${TEXTURE_ART}/public/xlarge_thin_button_01.png") no-repeat left top;
  color: #fff;
  font: inherit;
  cursor: pointer;
  image-rendering: pixelated;
}
.help-close:hover { background-image: url("${TEXTURE_ART}/public/xlarge_thin_button_02.png"); }
.help-close:active { background-image: url("${TEXTURE_ART}/public/xlarge_thin_button_03.png"); }
.help-sticks { position: absolute; inset: 0; pointer-events: none; }
.help-stick {
  position: absolute;
  width: 8px;
  background: url("/ui/textures/pattern/help_stick.png") no-repeat left top / 8px 100%;
  image-rendering: pixelated;
}
.help-stick-line {
  position: absolute;
  left: 4px;
  width: 240px;
  margin-left: -120px;
  text-align: center;
  font: 11px/13px Tahoma, "Segoe UI", sans-serif;
  color: #fff;
  text-shadow: -1px 0 #000, 1px 0 #000, 0 -1px #000, 0 1px #000;
  white-space: nowrap;
}
.help-stick-left .help-stick-line { left: 12px; margin-left: 0; text-align: left; width: auto; }
`;

/** `taskbar_help_stick_01`..`08`, the eight pointers `HelpWindow.py` drops on
 *  the taskbar (`helpwindow.py:121-260`). `left` is the stick's x, `bottom`
 *  the gap under its natural 8x100 box (its `y` is `SCREEN_HEIGHT - up`, so
 *  the box ends `up - 100` off the floor), `height` the box after the
 *  `rect`'s negative top has pulled the line upward, and `lines` the shipped
 *  `HELP_*` captions at their own offsets below the natural top. */
const HELP_STICKS: readonly {
  readonly left: string;
  readonly bottom: number;
  readonly height: number;
  readonly lines: readonly (readonly [number, string])[];
  readonly align: "left" | "center";
}[] = [
  { left: "22px", bottom: 20, height: 100, align: "left",
    lines: [[-20, "Ingame Item Shop"]] },
  { left: "80px", bottom: 22, height: 130, align: "center",
    lines: [[12, "Hit Points (HP)"]] },
  { left: "120px", bottom: 7, height: 140, align: "center",
    lines: [[23, "Spell Points (SP)"]] },
  { left: "206px", bottom: 16, height: 170, align: "center",
    lines: [[51, "Experience"]] },
  { left: "calc(50% - 116px)", bottom: 16, height: 150, align: "center",
    lines: [[32, "Function of the Left Mouse button"]] },
  { left: "calc(50% + 206px)", bottom: 16, height: 150, align: "center",
    lines: [[32, "Function of the Right Mouse button"]] },
  { left: "49.5%", bottom: 16, height: 170, align: "center",
    lines: [[51, "Fast Access Fields"]] },
  { left: "calc(100% - 81px)", bottom: 16, height: 128, align: "center",
    lines: [
      [-20, "System buttons"],
      [-5, "(Character Window, Inventory Window,"],
      [10, "List of Friends and Options)"],
    ] },
];

const installStyle = styleInstaller(STYLE_ID, STYLE);

export interface HelpActions {
  /** Mirrors `SetCloseEvent` without introducing a server request. */
  onClose?: () => void;
}

/** 2014's HelpWindow: its float text, its taskbar pointers and its close
 *  button over the UI curtain, local to the page and closed by H, Escape or the
 *  button (`uihelp.py:80-132`). */
export class HelpPanel {
  private readonly root: HTMLElement;
  private closeEvent: (() => void) | undefined;

  constructor(container: HTMLElement, actions: HelpActions = {}) {
    const doc = container.ownerDocument;
    installStyle(doc);
    this.root = elem(doc, "section", "help-window");
    this.root.hidden = true;
    // Native `Open` locks the window and gives it keyboard ownership. A
    // focusable section provides that same ownership without stealing focus
    // from the game while the panel is closed (`uihelp.py:93-100`).
    this.root.tabIndex = -1;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "true");
    this.root.setAttribute("aria-label", "Help");

    for (const [row, text] of HELP_ROWS) {
      const line = elem(doc, "div", "help-line", text);
      line.style.top = `${(50 + 20 * row) / 6}%`;
      this.root.append(line);
    }

    const sticks = elem(doc, "div", "help-sticks");
    sticks.setAttribute("aria-hidden", "true");
    for (const stick of HELP_STICKS) {
      const pointer = elem(doc, "div", `help-stick help-stick-${stick.align}`);
      pointer.style.left = stick.left;
      pointer.style.bottom = `${stick.bottom}px`;
      pointer.style.height = `${stick.height}px`;
      for (const [at, text] of stick.lines) {
        const line = elem(doc, "span", "help-stick-line", text);
        line.style.bottom = `${100 - at}px`;
        pointer.append(line);
      }
      sticks.append(pointer);
    }
    this.root.append(sticks);

    const close = elem(doc, "button", "help-close", "Close");
    close.type = "button";
    close.addEventListener("click", () => this.close());
    this.root.append(close);

    this.closeEvent = actions.onClose;
    this.root.addEventListener("keydown", (event) => {
      // `OnKeyDown` handles DIK_H and the native escape/exit hooks close the
      // same local window (`uihelp.py:101-132`). Enter intentionally does
      // nothing, matching `OnIMEReturn`'s `True` return.
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.key.toLowerCase() !== "h" && event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });
    container.append(this.root);
  }

  get showing(): boolean { return !this.root.hidden; }
  get element(): HTMLElement { return this.root; }
  open(): void {
    this.root.hidden = false;
    this.root.focus();
  }

  /** Close once and notify the composition root, like `SetCloseEvent`. */
  close(): void {
    if (this.root.hidden) return;
    this.root.hidden = true;
    this.closeEvent?.();
  }

  toggle(): void {
    if (this.showing) this.close();
    else this.open();
  }

  setCloseEvent(event: (() => void) | undefined): void {
    this.closeEvent = event;
  }
}
